import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Upload, FileAudio, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ConsentDialog } from "@/components/record/ConsentDialog";
import { CourseMetaForm } from "@/components/record/CourseMetaForm";

const OK = ["mp3", "wav", "m4a"];

export default function ImportAudio() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  const [subject, setSubject] = useState("Autre");
  const [consentOpen, setConsentOpen] = useState(false);
  const [progress, setProgress] = useState(null);
  const [drag, setDrag] = useState(false);

  const pick = (f) => {
    if (!f) return;
    const ext = f.name.split(".").pop().toLowerCase();
    if (!OK.includes(ext)) return toast.error("Format non supporté. Choisis un fichier MP3, WAV ou M4A.");
    if (f.size > 300 * 1024 * 1024) return toast.error("Fichier trop volumineux (300 Mo maximum).");
    setFile(f);
    if (!title) setTitle(f.name.replace(/\.[^.]+$/, ""));
  };

  const send = async () => {
    setConsentOpen(false);
    const fd = new FormData();
    fd.append("audio", file);
    fd.append("title", title);
    fd.append("subject", subject);
    fd.append("consent", "true");
    setProgress(0);
    try {
      const { data } = await api.post("/courses/import", fd, { timeout: 0, onUploadProgress: (e) => e.total && setProgress(Math.round((e.loaded / e.total) * 100)) });
      refresh();
      toast.success("Fichier reçu ! La transcription commence.");
      navigate(`/app/cours/${data.course_id}`);
    } catch (e) {
      toast.error(errMsg(e));
      setProgress(null);
    }
  };

  return (
    <div data-testid="import-page" className="max-w-3xl">
      <p className="eyebrow">Mode importation</p>
      <h1 className="mt-2 text-3xl font-extrabold tracking-tight sm:text-4xl">Importer un fichier audio</h1>
      <p className="mt-2 text-sm text-muted-foreground">MP3, WAV ou M4A, jusqu'à 300 Mo. Les fichiers longs sont découpés et transcrits automatiquement, puis l'audio est supprimé.</p>
      <div className="mt-8"><CourseMetaForm title={title} setTitle={setTitle} subject={subject} setSubject={setSubject} disabled={progress !== null} /></div>
      <div
        data-testid="import-dropzone"
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); pick(e.dataTransfer.files?.[0]); }}
        className={`mt-6 flex cursor-pointer flex-col items-center justify-center rounded-3xl border-2 border-dashed px-6 py-16 text-center transition-colors ${drag ? "border-primary bg-accent" : "hover:border-primary/50"}`}
      >
        <input ref={inputRef} type="file" accept=".mp3,.wav,.m4a,audio/mpeg,audio/wav,audio/mp4,audio/x-m4a" className="hidden" data-testid="import-file-input" onChange={(e) => pick(e.target.files?.[0])} />
        {file ? (
          <div className="flex items-center gap-3">
            <FileAudio className="h-8 w-8 text-primary" />
            <div className="text-left"><p className="font-semibold" data-testid="import-file-name">{file.name}</p><p className="text-xs text-muted-foreground">{(file.size / 1024 / 1024).toFixed(1)} Mo</p></div>
            <button data-testid="import-file-remove" onClick={(e) => { e.stopPropagation(); setFile(null); }} className="rounded-full p-1.5 hover:bg-muted"><X className="h-4 w-4" /></button>
          </div>
        ) : (
          <>
            <Upload className="h-9 w-9 text-primary" />
            <p className="mt-4 font-semibold">Glisse ton fichier ici ou clique pour le choisir</p>
            <p className="mt-1 text-sm text-muted-foreground">MP3 · WAV · M4A</p>
          </>
        )}
      </div>
      {progress !== null && (
        <div className="mt-5" data-testid="import-upload-progress">
          <div className="flex justify-between text-xs text-muted-foreground"><span>Envoi sécurisé…</span><span>{progress} %</span></div>
          <div className="mt-1.5 h-2 overflow-hidden rounded-full bg-muted"><div className="bg-brand h-full transition-[width]" style={{ width: `${progress}%` }} /></div>
        </div>
      )}
      <button data-testid="import-submit-button" disabled={!file || progress !== null} onClick={() => setConsentOpen(true)}
        className="bg-brand mt-6 inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold text-white disabled:opacity-50">
        {progress !== null ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Générer mes notes
      </button>
      <ConsentDialog open={consentOpen} onOpenChange={setConsentOpen} onConfirm={send} mode="import" />
    </div>
  );
}
